/* eslint-disable no-unused-vars */
import { useState } from 'react';
import {
    Bold,
    Italic,
    Underline,
    Link,
    Image,
    Smile,
    AtSign,
    ChevronDown
} from 'lucide-react';
import PropTypes from 'prop-types';

const CommentSection = ({ 
    blogId = "1",
    comments = [],
    status = "idle",
    currentUser = null 
}) => {
    const [formData, setFormData] = useState({
        name: currentUser?.displayName || '',
        comment: ''
    });

    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editContent, setEditContent] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.comment.trim()) return;
        // Handle submit logic here
        setFormData(prev => ({ ...prev, comment: '' }));
    };

    const handleEdit = (comment) => {
        setEditingCommentId(comment.id);
        setEditContent(comment.content);
    };

    const handleUpdateComment = (commentId) => {
        if (!editContent.trim()) return;
        // Handle update logic here
        setEditingCommentId(null);
        setEditContent('');
    };

    const handleDelete = (commentId) => {
        if (window.confirm('Are you sure you want to delete this comment?')) {
            // Handle delete logic here
        }
    };

    const formatDate = (timestamp) => {
        return '2 days ago'; // Simplified for demo
    };

    if (status === 'loading') {
        return <div className="flex justify-center py-8">Loading...</div>;
    }

    return (
        <div className="w-full max-w-3xl mx-auto px-4 sm:px-6">
            {/* Comment Editor */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-6 sm:mb-8">
                {!currentUser && (
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Your name"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full p-2 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                )}
                <textarea
                    placeholder="Add comment..."
                    value={formData.comment}
                    onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                    className="w-full min-h-[100px] mb-2 p-2 bg-transparent outline-none resize-none"
                />
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-t border-gray-200 pt-2 gap-3 sm:gap-0">
                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                        <button className="p-1.5 sm:p-2 hover:bg-gray-200 rounded">
                            <Bold size={18} className="text-gray-600" />
                        </button>
                        <button className="p-1.5 sm:p-2 hover:bg-gray-200 rounded">
                            <Italic size={18} className="text-gray-600" />
                        </button>
                        <button className="p-1.5 sm:p-2 hover:bg-gray-200 rounded">
                            <Underline size={18} className="text-gray-600" />
                        </button>
                        <button className="p-1.5 sm:p-2 hover:bg-gray-200 rounded">
                            <Link size={18} className="text-gray-600" />
                        </button>
                        <button className="p-1.5 sm:p-2 hover:bg-gray-200 rounded">
                            <Image size={18} className="text-gray-600" />
                        </button>
                        <button className="p-1.5 sm:p-2 hover:bg-gray-200 rounded">
                            <Smile size={18} className="text-gray-600" />
                        </button>
                        <button className="p-1.5 sm:p-2 hover:bg-gray-200 rounded">
                            <AtSign size={18} className="text-gray-600" />
                        </button>
                    </div>
                    <button
                        onClick={handleSubmit}
                        disabled={!formData.comment.trim()}
                        className="w-full sm:w-auto bg-green-500 text-white px-6 py-2 rounded-full hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        Submit
                    </button>
                </div>
            </div>

            {/* Comments Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                    Comments <span className="bg-green-500 text-white text-sm px-2 py-0.5 rounded-full">{comments.length}</span>
                </h2>
                <button className="flex items-center gap-2 text-gray-600 hover:text-gray-800">
                    <span className="text-sm">Most recent</span>
                    <ChevronDown size={16} />
                </button>
            </div>

            {/* Comments List */}
            <div className="space-y-6">
                {comments.length === 0 ? (
                    <p className="text-gray-500 italic">Be the first to comment!</p>
                ) : (
                    comments.map(comment => (
                        <div key={comment.id} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <img
                                src={`https://eu.ui-avatars.com/api/?name=${comment.userName}`}
                                alt={comment.userName}
                                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                            />
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <span className="font-semibold">{comment.userName}</span>
                                    <span className="text-gray-500 text-sm">{formatDate(comment.createdAt)}</span>
                                </div>
                                {editingCommentId === comment.id ? (
                                    <div className="mb-2">
                                        <textarea
                                            value={editContent}
                                            onChange={(e) => setEditContent(e.target.value)}
                                            className="w-full p-2 border rounded-lg"
                                        />
                                        <div className="flex gap-2 mt-2">
                                            <button
                                                onClick={() => handleUpdateComment(comment.id)}
                                                className="text-green-500 hover:text-green-600"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setEditingCommentId(null)}
                                                className="text-gray-500 hover:text-gray-600"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-gray-800 mb-2">{comment.content}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-4 text-sm">
                                    {currentUser?.uid === comment.userId && (
                                        <>
                                            <button
                                                onClick={() => handleEdit(comment)}
                                                className="text-gray-500 hover:text-gray-700"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(comment.id)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                Delete
                                            </button>
                                        </>
                                    )}
                                    <button className="text-gray-500 hover:text-gray-700">
                                        Reply
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

CommentSection.propTypes = {
    blogId: PropTypes.string,
    comments: PropTypes.array.isRequired,
    status: PropTypes.string.isRequired,
    currentUser: PropTypes.object
};

export default CommentSection;